import { useState } from "react";
import {
  Megaphone, Sparkles, Loader2, RefreshCcw, Copy, Check,
  Layers, GitBranch, Grid3x3, ImageIcon, Download,
} from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const PRODUCTS = ["FlipCost", "SpaceCost", "MyPlanCost"];
const GOALS = ["Get sign-ups", "Try the demo", "Drive to pricing", "Brand awareness"];
const AUDIENCES = [
  "Property investors planning a flip",
  "Homeowners renovating to sell",
  "Homeowners renovating to live",
  "DIY tradespeople",
  "Real estate agents",
];
const REGIONS = ["", "South Africa", "USA", "Australia", "New Zealand", "United Kingdom", "Worldwide"];
const TONES = ["Professional", "Punchy", "Educational", "Witty", "Aspirational"];
const CTAS = ["Start your first project", "Try the demo", "See pricing", "Sign up free trial", "Custom"];

const MODES = [
  { id: "standard", label: "6 single ads",  icon: Grid3x3 },
  { id: "carousel", label: "Carousel pack", icon: Layers },
  { id: "ab_pair",  label: "A/B pairs",     icon: GitBranch },
];

export default function AdvertisingTab() {
  // === Inputs ===
  const [product, setProduct] = useState("FlipCost");
  const [goal, setGoal] = useState("Get sign-ups");
  const [audience, setAudience] = useState(AUDIENCES[0]);
  const [region, setRegion] = useState("South Africa");
  const [tone, setTone] = useState("Punchy");
  const [angle, setAngle] = useState("");
  const [ctaPick, setCtaPick] = useState(CTAS[0]);
  const [customCta, setCustomCta] = useState("");
  const [brandVoiceNotes, setBrandVoiceNotes] = useState("");
  const [mode, setMode] = useState("standard");

  // === State ===
  const [generating, setGenerating] = useState(false);
  const [regeneratingIdx, setRegeneratingIdx] = useState(null);
  const [imageBusyIdx, setImageBusyIdx] = useState(null);
  const [variants, setVariants] = useState(null);
  // Image dataURLs indexed by variant position. Decoupled from `variants`
  // so a Re-roll only invalidates a single image without redrawing others.
  const [variantImages, setVariantImages] = useState({});

  const ctaValue = ctaPick === "Custom" ? customCta.trim() : ctaPick;

  const payload = () => ({
    product, goal, audience, region, tone, angle,
    cta: ctaValue,
    brand_voice_notes: brandVoiceNotes,
    mode,
  });

  const generate = async () => {
    if (!ctaValue) {
      toast.error("Pick a CTA (or fill in the Custom one)");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await http.post("/admin/ads/generate", payload());
      setVariants(data.variants);
      setVariantImages({});  // new copy → wipe stale images
      toast.success(`Generated ${data.variants.length} variants`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const regenerateOne = async (idx) => {
    setRegeneratingIdx(idx);
    try {
      const vibe = variants?.[idx]?.vibe || null;
      const { data } = await http.post("/admin/ads/regenerate-one", {
        ...payload(),
        vibe_hint: vibe,
      });
      setVariants((vs) => vs.map((v, i) => (i === idx ? data.variant : v)));
      // A new copy means the old image no longer matches — drop it.
      setVariantImages((m) => {
        const next = { ...m };
        delete next[idx];
        return next;
      });
      toast.success("Variant regenerated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regeneration failed");
    } finally {
      setRegeneratingIdx(null);
    }
  };

  const generateImage = async (idx) => {
    setImageBusyIdx(idx);
    try {
      const { data } = await http.post("/admin/ads/generate-image", {
        inputs: payload(),
        variant: variants[idx],
      });
      setVariantImages((m) => ({ ...m, [idx]: data.image }));
      toast.success("Image generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Image generation failed");
    } finally {
      setImageBusyIdx(null);
    }
  };

  return (
    <div data-testid="admin-advertising-tab" className="space-y-6">
      {/* ----- Inputs ----- */}
      <section className="fc-card p-6 space-y-4">
        <header className="flex items-start gap-2">
          <Megaphone className="w-5 h-5 text-sage-700 mt-0.5" />
          <div>
            <h3 className="fc-heading text-lg text-[#1C1C1C]">Campaign brief</h3>
            <p className="text-xs text-[#A8A29E] mt-0.5">
              Fill in the brief and Claude Sonnet 4.5 will generate {mode === "standard" ? "6 distinct ads" : mode === "carousel" ? "a 6-slide carousel" : "3 A/B-test pairs (6 ads)"} matched to your audience and tone.
            </p>
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Product">
            <Select value={product} onChange={setProduct} options={PRODUCTS} tid="ad-product" />
          </Field>
          <Field label="Campaign goal">
            <Select value={goal} onChange={setGoal} options={GOALS} tid="ad-goal" />
          </Field>
          <Field label="Target audience">
            <Select value={audience} onChange={setAudience} options={AUDIENCES} tid="ad-audience" />
          </Field>
          <Field label="Region focus (optional)">
            <Select
              value={region}
              onChange={setRegion}
              options={REGIONS.map((r) => r || "— No region focus —")}
              tid="ad-region"
            />
          </Field>
          <Field label="Tone">
            <Select value={tone} onChange={setTone} options={TONES} tid="ad-tone" />
          </Field>
          <Field label="Call-to-action">
            <Select value={ctaPick} onChange={setCtaPick} options={CTAS} tid="ad-cta-pick" />
          </Field>
          {ctaPick === "Custom" && (
            <Field label="Custom CTA text" full>
              <Input
                data-testid="ad-cta-custom"
                value={customCta}
                onChange={(e) => setCustomCta(e.target.value)}
                placeholder="e.g. Plan your next flip"
                className="fc-input h-10"
              />
            </Field>
          )}
          <Field label="Key angle / unique value (optional)" full>
            <Input
              data-testid="ad-angle"
              value={angle}
              onChange={(e) => setAngle(e.target.value)}
              placeholder='e.g. "Avoid the R200k surprise"'
              className="fc-input h-10"
            />
          </Field>
          <Field label="Brand voice notes (optional)" full>
            <Textarea
              data-testid="ad-brand-voice"
              value={brandVoiceNotes}
              onChange={(e) => setBrandVoiceNotes(e.target.value)}
              placeholder="e.g. Always say 'plan with confidence'. Never say 'free' or 'guarantee'."
              className="fc-input min-h-[68px]"
            />
          </Field>
        </div>

        {/* Mode pills */}
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <span className="fc-label text-xs">Output mode</span>
          <div className="inline-flex p-1 rounded-full bg-black/[0.04] border border-black/[0.06]">
            {MODES.map(({ id, label, icon: Icon }) => {
              const active = mode === id;
              return (
                <button
                  key={id}
                  type="button"
                  data-testid={`ad-mode-${id}`}
                  onClick={() => setMode(id)}
                  className={`px-3 h-8 rounded-full inline-flex items-center gap-1.5 text-xs font-medium transition-colors ${
                    active
                      ? "bg-white text-[#1C1C1C] shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
                      : "text-[#78716C] hover:text-[#1C1C1C]"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            data-testid="ad-generate-btn"
            onClick={generate}
            disabled={generating}
            className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] gap-1.5 h-10 px-4"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            {generating ? "Generating with Claude…" : "Generate ads"}
          </Button>
        </div>
      </section>

      {/* ----- Output ----- */}
      {variants && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="fc-heading text-lg text-[#1C1C1C]">
              {mode === "carousel" ? "Carousel slides" : "Ad variants"}{" "}
              <span className="text-[#A8A29E] text-sm">· {variants.length}</span>
            </h3>
            <span className="text-[11px] text-[#A8A29E]">Generated by Claude Sonnet 4.5</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {variants.map((v, i) => (
              <VariantCard
                key={`variant-${i}`}
                idx={i}
                v={v}
                imageUrl={variantImages[i]}
                imageBusy={imageBusyIdx === i}
                regenerating={regeneratingIdx === i}
                onRegenerate={() => regenerateOne(i)}
                onGenerateImage={() => generateImage(i)}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ---------- helpers ----------
function Field({ label, full = false, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="fc-label mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function Select({ value, onChange, options, tid }) {
  return (
    <select
      data-testid={tid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="fc-input h-10 w-full rounded-md text-sm px-2"
    >
      {options.map((o) => (
        <option key={o} value={o.startsWith("— ") ? "" : o}>
          {o}
        </option>
      ))}
    </select>
  );
}

function VariantCard({ idx, v, imageUrl, imageBusy, regenerating, onRegenerate, onGenerateImage }) {
  const [copied, setCopied] = useState(false);
  const fullText = [
    v.hook,
    "",
    v.caption,
    "",
    v.hashtags.join(" "),
  ].join("\n");
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Clipboard blocked");
    }
  };
  const downloadImage = () => {
    if (!imageUrl) return;
    const a = document.createElement("a");
    a.href = imageUrl;
    // Sanitised filename from the hook; fall back to variant index.
    const stem = (v.hook || `ad-${idx + 1}`)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 40);
    a.download = `${stem || `ad-${idx + 1}`}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  return (
    <div
      className="fc-card p-5 space-y-3"
      data-testid={`ad-variant-${idx}`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium border bg-sage-100 text-sage-700 border-sage-200">
          <Sparkles className="w-3 h-3" /> {v.vibe || `Variant ${idx + 1}`}
        </span>
        <div className="flex flex-wrap gap-1.5 justify-end">
          <Button
            size="sm"
            variant="outline"
            data-testid={`ad-image-${idx}`}
            onClick={onGenerateImage}
            disabled={imageBusy}
            title="Generate a 1:1 photo to pair with this copy (Gemini Nano Banana)"
            className="h-7 px-2 text-xs gap-1 border-black/[0.12]"
          >
            {imageBusy ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <ImageIcon className="w-3 h-3" />
            )}
            {imageUrl ? "Re-image" : "Image"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            data-testid={`ad-regenerate-${idx}`}
            onClick={onRegenerate}
            disabled={regenerating}
            className="h-7 px-2 text-xs gap-1 border-black/[0.12]"
          >
            {regenerating ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <RefreshCcw className="w-3 h-3" />
            )}
            Re-roll
          </Button>
          <Button
            size="sm"
            variant="outline"
            data-testid={`ad-copy-${idx}`}
            onClick={copy}
            className="h-7 px-2 text-xs gap-1 border-black/[0.12]"
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            Copy
          </Button>
        </div>
      </div>

      {/* Image preview (only after the operator clicks "Image") */}
      {imageUrl && (
        <div className="relative group" data-testid={`ad-image-preview-${idx}`}>
          <img
            src={imageUrl}
            alt={v.hook}
            className="w-full aspect-square object-cover rounded-lg border border-black/[0.08]"
          />
          <button
            type="button"
            onClick={downloadImage}
            data-testid={`ad-image-download-${idx}`}
            title="Download PNG"
            className="absolute top-2 right-2 inline-flex items-center gap-1 text-[11px] font-medium bg-white/95 backdrop-blur-sm border border-black/[0.08] rounded-md h-7 px-2 text-[#1C1C1C] hover:bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Download className="w-3 h-3" /> PNG
          </button>
          <p className="text-[10px] text-[#A8A29E] mt-1.5 italic">
            Image is text-free on purpose — overlay your hook + brand in Canva/Figma.
          </p>
        </div>
      )}

      <div>
        <div className="fc-heading text-[#1C1C1C] text-base leading-snug">{v.hook}</div>
        <p className="text-sm text-[#27272A] mt-2 whitespace-pre-wrap leading-relaxed">
          {v.caption}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-black/[0.06]">
        <div>
          <div className="fc-label">CTA</div>
          <div className="text-xs text-[#1C1C1C] mt-0.5 font-medium">
            {v.cta_button || "—"}
          </div>
        </div>
        <div>
          <div className="fc-label">Visual direction</div>
          <div className="text-xs text-[#57534E] mt-0.5 leading-snug">
            {v.visual_direction || "—"}
          </div>
        </div>
      </div>

      {v.hashtags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {v.hashtags.map((h) => (
            <span
              key={h}
              className="text-[11px] text-sage-700 bg-sage-50 border border-sage-200 rounded-full px-2 py-0.5"
            >
              {h}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
